import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import { Icons } from '../Icons';
import { DisplaySetMessageListTooltip } from '../DisplaySetMessageListTooltip';
import { TooltipTrigger, TooltipContent, Tooltip } from '../Tooltip';

/**
 * Display a thumbnail for a display set.
 */
const ThumbnailComponent = ({
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  loadingProgress,
  loadedInstances,
  failedInstances,
  countIcon,
  messages,
  isActive,
  onClick,
  onDoubleClick,
  thumbnailType,
  modality,
  viewPreset = 'thumbnails',
  isHydratedForDerivedDisplaySet = false,
  isTracked = false,
  canReject = false,
  dragData = {},
  onReject = () => {},
  onClickUntrack = () => {},
  ThumbnailMenuItems = () => {},
}: withAppTypes): React.ReactNode => {
  // TODO: We should wrap our thumbnail to create a "DraggableThumbnail", as
  // this will still allow for "drag", even if there is no drop target for the
  // specified item.
  const [collectedProps, drag, dragPreview] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
  });

  const [lastTap, setLastTap] = useState(0);

  const renderLoadingProgress = () => {
    if (thumbnailType === 'thumbnailNoImage' || numInstances === 0) {
      return null;
    }

    const loaded = Number.isFinite(loadedInstances) ? loadedInstances : undefined;
    const failed = Number.isFinite(failedInstances) ? failedInstances : 0;
    const progressFromCounts =
      numInstances > 0 && loaded !== undefined ? (loaded + failed) / numInstances : undefined;
    const rawProgress = Number.isFinite(progressFromCounts)
      ? progressFromCounts
      : Number.isFinite(loadingProgress)
        ? loadingProgress
        : 0;
    const hasKnownProgress =
      Number.isFinite(progressFromCounts) || Number.isFinite(loadingProgress);
    const progress = Math.min(1, Math.max(0, rawProgress));
    const isIncomplete = failed > 0;
    const isReady = numInstances > 0 && loaded === numInstances && failed === 0 && progress === 1;
    let tooltip = 'Aguardando carregamento';

    if (isIncomplete) {
      tooltip = `Série incompleta — ${loaded ?? 0} de ${numInstances} imagens carregadas, ${failed} falharam`;
    } else if (isReady) {
      tooltip = `Série pronta para análise — ${loaded} de ${numInstances} imagens carregadas`;
    } else if (hasKnownProgress) {
      tooltip =
        loaded !== undefined
          ? `Carregando série: ${loaded} de ${numInstances} imagens`
          : `Carregando série: ${Math.round(progress * 100)}%`;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="mt-[2px] h-[3px] min-h-[3px] w-full flex-none overflow-hidden rounded-full bg-white/25"
            data-cy="series-loading-progress"
            data-loading-state={isIncomplete ? 'incomplete' : isReady ? 'ready' : 'loading'}
            role="progressbar"
            aria-label={tooltip}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={hasKnownProgress ? Math.round(progress * 100) : undefined}
          >
            <div
              className={classnames(
                'h-full rounded-full transition-[width]',
                isIncomplete
                  ? 'bg-destructive'
                  : isReady
                    ? 'bg-[var(--success-text)]'
                    : 'bg-highlight'
              )}
              style={{ width: `${progress * 100}%`, minHeight: '3px' }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  };

  const handleTouchEnd = e => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 300 && tapLength > 0) {
      onDoubleClick(e);
    } else {
      onClick(e);
    }
    setLastTap(currentTime);
  };

  const renderThumbnailPreset = () => {
    return (
      <div
        className={classnames(
          'flex h-full w-full flex-col items-center justify-center gap-[2px] p-[4px]',
          isActive && 'bg-popover rounded'
        )}
      >
        <div className="w-full max-w-[128px]">
          <div className="relative aspect-[128/114] w-full bg-black">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={imageAltText}
                className="h-full w-full rounded object-contain"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="bg-background h-full w-full rounded"></div>
            )}

            {/* bottom left */}
            <div className="absolute bottom-0 left-0 flex h-[14px] items-center gap-[4px] rounded-tr pt-[10px] pb-[10px] pr-[6px] pl-[5px]">
              <div
                className={classnames(
                  'h-[10px] w-[10px] rounded-[2px]',
                  isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
                  Number.isFinite(loadingProgress) && loadingProgress < 1 && 'bg-primary/25'
                )}
              ></div>
              <div
                className="text-[11px] font-semibold text-white"
                data-cy="series-modality-label"
              >
                {modality}
              </div>
            </div>

            {/* top right */}
            <div className="absolute top-0 right-0 flex items-center gap-[4px]">
              <DisplaySetMessageListTooltip
                messages={messages}
                id={`display-set-tooltip-${displaySetInstanceUID}`}
              />
              {isTracked && (
                <Tooltip>
                  <TooltipTrigger>
                    <div className="group">
                      <Icons.StatusTracking className="text-primary-light h-[15px] w-[15px] group-hover:hidden" />
                      <Icons.Cancel
                        className="text-primary-light hidden h-[15px] w-[15px] group-hover:block"
                        onClick={onClickUntrack}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div className="flex flex-1 flex-row">
                      <div className="flex-2 flex items-center justify-center pr-4">
                        <Icons.InfoLink className="text-primary" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <span>
                          <span className="text-white">
                            {isTracked ? 'Series is tracked' : 'Series is untracked'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {/* bottom right */}
            <div className="absolute bottom-0 right-0 flex items-center gap-[4px] p-[4px]">
              <ThumbnailMenuItems
                displaySetInstanceUID={displaySetInstanceUID}
                canReject={canReject}
                onReject={onReject}
              />
            </div>
          </div>
          {renderLoadingProgress()}
        </div>
        <div className="flex h-[52px] w-full max-w-[128px] flex-col justify-start pt-px">
          <Tooltip>
            <TooltipContent>{description}</TooltipContent>
            <TooltipTrigger>
              <div
                className="min-h-[18px] w-full overflow-hidden text-ellipsis whitespace-nowrap pb-0.5 pl-1 text-left text-[12px] font-normal leading-4 text-white"
                data-cy="series-description-label"
              >
                {description}
              </div>
            </TooltipTrigger>
          </Tooltip>
          <div className="flex h-[12px] items-center gap-[7px] overflow-hidden">
            <div className="text-muted-foreground pl-1 text-[11px]"> S:{seriesNumber}</div>
            <div className="text-muted-foreground text-[11px]">
              <div className="flex items-center gap-[4px]">
                {countIcon ? (
                  React.createElement(Icons[countIcon] || Icons.MissingIcon, { className: 'w-3' })
                ) : (
                  <Icons.InfoSeries className="w-3" />
                )}
                <div>{numInstances}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderListPreset = () => {
    return (
      <div
        className={classnames(
          'flex h-full w-full items-center justify-between pr-[8px] pl-[8px] pt-[4px] pb-[4px]',
          isActive && 'bg-popover rounded'
        )}
      >
        <div className="flex h-[32px] w-full min-w-0 flex-col gap-[2px] overflow-hidden">
          <div className="relative flex min-h-0 w-full flex-1 items-center gap-[8px] overflow-hidden">
            <div
              className={classnames(
                'h-[32px] w-[4px] min-w-[4px] rounded',
                isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
                Number.isFinite(loadingProgress) && loadingProgress < 1 && 'bg-primary/25'
              )}
            ></div>
            <div className="flex h-full w-[calc(100%-12px)] flex-col justify-start">
              <div className="flex items-center gap-[7px]">
                <div
                  className="text-[13px] font-semibold text-white"
                  data-cy="series-modality-label"
                >
                  {modality}
                </div>
                <Tooltip>
                  <TooltipContent>{description}</TooltipContent>
                  <TooltipTrigger className="w-full overflow-hidden">
                    <div
                      className="max-w-[160px] overflow-hidden overflow-ellipsis whitespace-nowrap text-left text-[13px] font-normal text-white"
                      data-cy="series-description-label"
                    >
                      {description}
                    </div>
                  </TooltipTrigger>
                </Tooltip>
              </div>

              <div className="flex h-[12px] items-center gap-[7px] overflow-hidden">
                <div className="text-muted-foreground text-[12px]"> S:{seriesNumber}</div>
                <div className="text-muted-foreground text-[12px]">
                  <div className="flex items-center gap-[4px]">
                    {' '}
                    {countIcon ? (
                      React.createElement(Icons[countIcon] || Icons.MissingIcon, {
                        className: 'w-3',
                      })
                    ) : (
                      <Icons.InfoSeries className="w-3" />
                    )}
                    <div>{numInstances}</div>
                  </div>
                </div>
              </div>
            </div>
            {renderLoadingProgress()}
          </div>
          {renderLoadingProgress()}
        </div>
        <div className="flex h-full items-center gap-[4px]">
          <DisplaySetMessageListTooltip
            messages={messages}
            id={`display-set-tooltip-${displaySetInstanceUID}`}
          />
          {isTracked && (
            <Tooltip>
              <TooltipTrigger>
                <div className="group">
                  <Icons.StatusTracking className="text-primary-light h-[20px] w-[15px] group-hover:hidden" />
                  <Icons.Cancel
                    className="text-primary-light hidden h-[15px] w-[15px] group-hover:block"
                    onClick={onClickUntrack}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div className="flex flex-1 flex-row">
                  <div className="flex-2 flex items-center justify-center pr-4">
                    <Icons.InfoLink className="text-primary" />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <span>
                      <span className="text-white">
                        {isTracked ? 'Series is tracked' : 'Series is untracked'}
                      </span>
                    </span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
          <ThumbnailMenuItems
            displaySetInstanceUID={displaySetInstanceUID}
            canReject={canReject}
            onReject={onReject}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={classnames(
        className,
        'bg-muted hover:bg-primary/30 group flex cursor-pointer select-none flex-col rounded outline-none',
        viewPreset === 'thumbnails' && 'h-[176px] w-full max-w-[135px]',
        viewPreset === 'list' && 'h-[40px] w-full'
      )}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={
        thumbnailType === 'thumbnailNoImage'
          ? 'study-browser-thumbnail-no-image'
          : 'study-browser-thumbnail'
      }
      data-series={seriesNumber}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={handleTouchEnd}
      role="button"
    >
      <div
        ref={drag}
        className="h-full w-full"
      >
        {viewPreset === 'thumbnails' && renderThumbnailPreset()}
        {viewPreset === 'list' && renderListPreset()}
      </div>
    </div>
  );
};

ThumbnailComponent.propTypes = {
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  /**
   * Data the thumbnail should expose to a receiving drop target. Use a matching
   * `dragData.type` to identify which targets can receive this draggable item.
   * If this is not set, drag-n-drop will be disabled for this thumbnail.
   *
   * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
   */
  dragData: PropTypes.shape({
    /** Must match the "type" a dropTarget expects */
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: PropTypes.any,
  numInstances: PropTypes.number.isRequired,
  loadingProgress: PropTypes.number,
  loadedInstances: PropTypes.number,
  failedInstances: PropTypes.number,
  pendingInstances: PropTypes.number,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  modality: PropTypes.string,
  isHydratedForDerivedDisplaySet: PropTypes.bool,
  isTracked: PropTypes.bool,
  onClickUntrack: PropTypes.func,
  countIcon: PropTypes.string,
  thumbnailType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage']),
};

// The StudyBrowser panel rebuilds its whole displaySets array (and every item in it) on
// every progress tick while a study is prefetching in the background — so a fresh array
// reference reaches every Thumbnail on every tick regardless. Without memoization, that
// re-renders every visible thumbnail every tick, even the ones whose own series didn't
// change. This custom comparator (same pattern as OHIFCornerstoneViewport's `areEqual`,
// the only other React.memo in this codebase) only checks the primitive/data fields that
// actually affect what's drawn, so an unrelated thumbnail's re-render is skipped even
// though its containing array is new. Function props (onClick/onDoubleClick/onClickUntrack/
// ThumbnailMenuItems) are intentionally not compared: ThumbnailList rebinds them fresh
// every render via `.bind()`, but they always wrap the same underlying (already
// useCallback-stabilized) handler with the same displaySetInstanceUID, so the "stale"
// props kept by skipping a re-render behave identically to the "new" ones.
function areEqualThumbnail(prevProps, nextProps) {
  return (
    prevProps.displaySetInstanceUID === nextProps.displaySetInstanceUID &&
    prevProps.className === nextProps.className &&
    prevProps.imageSrc === nextProps.imageSrc &&
    prevProps.imageAltText === nextProps.imageAltText &&
    prevProps.description === nextProps.description &&
    prevProps.seriesNumber === nextProps.seriesNumber &&
    prevProps.numInstances === nextProps.numInstances &&
    prevProps.loadingProgress === nextProps.loadingProgress &&
    prevProps.loadedInstances === nextProps.loadedInstances &&
    prevProps.failedInstances === nextProps.failedInstances &&
    prevProps.pendingInstances === nextProps.pendingInstances &&
    prevProps.countIcon === nextProps.countIcon &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.thumbnailType === nextProps.thumbnailType &&
    prevProps.modality === nextProps.modality &&
    prevProps.viewPreset === nextProps.viewPreset &&
    prevProps.isHydratedForDerivedDisplaySet === nextProps.isHydratedForDerivedDisplaySet &&
    prevProps.isTracked === nextProps.isTracked &&
    prevProps.canReject === nextProps.canReject &&
    JSON.stringify(prevProps.messages) === JSON.stringify(nextProps.messages) &&
    JSON.stringify(prevProps.dragData) === JSON.stringify(nextProps.dragData)
  );
}

const Thumbnail = React.memo(ThumbnailComponent, areEqualThumbnail);

export { Thumbnail };
